// Agent Command stage — action-type catalog.
//
// Mirrors api/_lib/agent-workspace/actions.ts ACTION_TYPES exactly. That
// file is server-only (imports Daily/meeting internals) and cannot be
// imported from browser code, so this list is a documented, deliberate
// duplicate of the five values — not an invented fourth path. If the
// server list ever drifts from this one, the server is the source of
// truth: action-requests.ts rejects anything outside its own Set with
// `invalid_action_type`, so a stale client entry fails closed, never
// silently succeeds.
//
// `executionMode` is not decorative copy — each string is a direct,
// honest paraphrase of what api/_lib/agent-workspace/actions.ts
// executeApprovedAction() actually does for that action type once an
// operator approves. Nothing here claims an outcome the server code
// doesn't already implement.

export type AgentActionTypeValue =
  | 'draft_email'
  | 'create_secure_access_code'
  | 'prepare_portal_invite'
  | 'create_daily_meeting'
  | 'create_post_job'

export interface AgentActionTypeOption {
  value: AgentActionTypeValue
  label: string
  needsTargets: boolean
  riskTier: 'low' | 'medium' | 'high'
  executionMode: string
}

export const AGENT_ACTION_TYPES: AgentActionTypeOption[] = [
  {
    value: 'draft_email',
    label: 'Draft email',
    needsTargets: true,
    riskTier: 'low',
    executionMode: 'On approval: prepares a draft artifact only (prepared_not_sent). No send channel exists in this codebase — nothing is emailed from here.',
  },
  {
    value: 'create_post_job',
    label: 'Create Post Job',
    needsTargets: false,
    riskTier: 'medium',
    executionMode: 'On approval: creates a real sq_post_jobs row (captured). That job’s own review_required gate still applies before delivery.',
  },
  {
    value: 'create_daily_meeting',
    label: 'Create Daily meeting',
    needsTargets: false,
    riskTier: 'medium',
    executionMode: 'On approval: creates a real Daily room through the existing Meeting Mode gate if Daily is configured — otherwise blocked_provider, honestly, with no room created.',
  },
  {
    value: 'create_secure_access_code',
    label: 'Create secure access code',
    needsTargets: false,
    riskTier: 'high',
    executionMode: 'On approval: routed to /square/olivium/secure-access as blocked_on_gate — does not execute live from here until that module’s own gate is wired. Nothing is issued from this page.',
  },
  {
    value: 'prepare_portal_invite',
    label: 'Prepare portal invite',
    needsTargets: true,
    riskTier: 'high',
    executionMode: 'On approval: routed to /square/onboarding/secure-invites as blocked_on_gate — does not execute live from here until that module’s own gate is wired. Nothing is sent from this page.',
  },
]

export function actionTypeOption(value: string): AgentActionTypeOption | null {
  return AGENT_ACTION_TYPES.find((o) => o.value === value) ?? null
}
